/** 嵌入主题契约与校验。颜色只允许白名单 token 和无表达式的字面值。 */
import { MAX_ENUM_LENGTH, MAX_THEME_COLOR_LENGTH, THEMES } from "./constants.js";
import { isRecord, readEnum, readText } from "./primitives.js";
/** 唯一 token 白名单，同时供 iframe 将语义键映射为 CSS 变量。 */
export const EMBEDDED_THEME_TOKEN_PROPERTIES = Object.freeze({
    background: "--background",
    foreground: "--foreground",
    card: "--card",
    cardForeground: "--card-foreground",
    popover: "--popover",
    popoverForeground: "--popover-foreground",
    primary: "--primary",
    primaryForeground: "--primary-foreground",
    secondary: "--secondary",
    secondaryForeground: "--secondary-foreground",
    muted: "--muted",
    mutedForeground: "--muted-foreground",
    accent: "--accent",
    accentForeground: "--accent-foreground",
    destructive: "--destructive",
    destructiveForeground: "--destructive-foreground",
    success: "--success",
    successForeground: "--success-foreground",
    warning: "--warning",
    warningForeground: "--warning-foreground",
    border: "--border",
    input: "--input",
    ring: "--ring",
    chart1: "--chart-1",
    chart2: "--chart-2",
    chart3: "--chart-3",
    chart4: "--chart-4",
    chart5: "--chart-5",
    sidebar: "--sidebar",
    sidebarForeground: "--sidebar-foreground",
    sidebarPrimary: "--sidebar-primary",
    sidebarPrimaryForeground: "--sidebar-primary-foreground",
    sidebarAccent: "--sidebar-accent",
    sidebarAccentForeground: "--sidebar-accent-foreground",
    sidebarBorder: "--sidebar-border",
    sidebarRing: "--sidebar-ring",
});
// 只接受数值成分，不接受 CSS 变量、相对颜色、calc、URL 或任意声明。
const whitespace = "[\\t\\n\\f\\r ]";
const number = "[+-]?(?:\\d+(?:\\.\\d+)?|\\.\\d+)";
const percentage = `${number}%`;
const numberOrPercentage = `${number}%?`;
const hue = `${number}(?:deg|grad|rad|turn)?`;
const alpha = `(?:${whitespace}*/${whitespace}*${numberOrPercentage})?`;
const commaAlpha = `(?:${whitespace}*,${whitespace}*${numberOrPercentage})?`;
const rgbComma = [number, percentage].map((component) => `${component}${whitespace}*,${whitespace}*${component}${whitespace}*,${whitespace}*${component}${commaAlpha}`);
const colorFunctions = new RegExp(`^(?:rgba?\\(${whitespace}*(?:${rgbComma.join("|")}|${numberOrPercentage}${whitespace}+${numberOrPercentage}${whitespace}+${numberOrPercentage}${alpha})${whitespace}*\\)` +
    `|hsla?\\(${whitespace}*(?:${hue}${whitespace}*,${whitespace}*${percentage}${whitespace}*,${whitespace}*${percentage}${commaAlpha}|${hue}${whitespace}+${percentage}${whitespace}+${percentage}${alpha})${whitespace}*\\)` +
    `|oklch\\(${whitespace}*${numberOrPercentage}${whitespace}+${numberOrPercentage}${whitespace}+${hue}${alpha}${whitespace}*\\))$`, "i");
const hexColor = /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i;
function parseThemeColor(value) {
    const text = readText(value, MAX_THEME_COLOR_LENGTH)?.trim();
    return text && (hexColor.test(text) || /^transparent$/i.test(text) || colorFunctions.test(text))
        ? text
        : null;
}
/** 校验并复制主题快照；任一 token 非法时拒绝整份主题。 */
export function parseEmbeddedTheme(value) {
    if (!isRecord(value)) {
        return null;
    }
    const mode = readEnum(value.mode, THEMES, MAX_ENUM_LENGTH);
    if (mode === null) {
        return null;
    }
    if (value.tokens === undefined) {
        return { mode };
    }
    if (!isRecord(value.tokens)) {
        return null;
    }
    const entries = Object.entries(value.tokens);
    if (entries.length > Object.keys(EMBEDDED_THEME_TOKEN_PROPERTIES).length) {
        return null;
    }
    const tokens = {};
    for (const [key, value] of entries) {
        if (!Object.hasOwn(EMBEDDED_THEME_TOKEN_PROPERTIES, key)) {
            return null;
        }
        const color = parseThemeColor(value);
        if (color === null) {
            return null;
        }
        tokens[key] = color;
    }
    return { mode, tokens };
}
export function isEmbeddedTheme(value) {
    return parseEmbeddedTheme(value) !== null;
}
//# sourceMappingURL=theme.js.map